import { useEffect, useState } from "react";
import { api, formatApiError, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Plus, Trash2, UserPlus, Shield, Briefcase, Users as UsersIcon, User } from "lucide-react";

const roleInfo = {
  super_admin: { label: "Owner", color: "bg-[color:var(--brand)] text-white", icon: <Shield className="w-3.5 h-3.5" />, desc: "Full access — cannot be deleted by others." },
  admin: { label: "Admin", color: "bg-[color:var(--deep)] text-white", icon: <Briefcase className="w-3.5 h-3.5" />, desc: "Manage rooms, users, settings and all operations." },
  hotel_manager: { label: "Hotel Manager", color: "bg-blue-600 text-white", icon: <UsersIcon className="w-3.5 h-3.5" />, desc: "View everything, log expenses, run reports." },
  receptionist: { label: "Receptionist", color: "bg-amber-500 text-white", icon: <User className="w-3.5 h-3.5" />, desc: "Bookings, check-in/out and room service." },
};

export default function UsersPage() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "receptionist" });

  const load = async () => { const { data } = await api.get("/admin/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("Team member added");
      setOpen(false); setForm({ email: "", password: "", name: "", role: "receptionist" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const canCreate = current?.role === "super_admin" || current?.role === "admin";
  const canDelete = current?.role === "super_admin";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-[color:var(--deep)]">Team</h1>
          <p className="text-sm text-[color:var(--muted-text)] mt-1">Roles: Owner · Admin · Manager · Receptionist.</p>
        </div>
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="new-user-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><Plus className="w-4 h-4 mr-1" /> New team member</Button>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u) => {
          const info = roleInfo[u.role] || { label: u.role, color: "bg-slate-500 text-white", icon: null, desc: "" };
          return (
            <Card key={u.id} data-testid={`user-card-${u.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-heading text-lg text-[color:var(--deep)] truncate">{u.name}</p>
                    <p className="text-xs text-[color:var(--muted-text)] mt-0.5 truncate">{u.email}</p>
                  </div>
                  <Badge className={`${info.color} gap-1 shrink-0`}>{info.icon}{info.label}</Badge>
                </div>
                <p className="text-xs text-[color:var(--muted-text)] mt-3">{info.desc}</p>
                <p className="text-xs text-[color:var(--muted-text)] mt-2">Since {formatDateTime(u.created_at)}</p>
                {canDelete && u.id !== current.id && u.role !== "super_admin" && (
                  <Button variant="destructive" size="sm" onClick={() => remove(u.id)} className="mt-3" data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>Add team member</DialogTitle>
            <DialogDescription>They will use this email + password to sign in at /admin/login.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="hotel_manager">Hotel Manager</SelectItem>
                  <SelectItem value="receptionist">Receptionist</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[color:var(--muted-text)] mt-2">{roleInfo[form.role]?.desc}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-user-btn" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]"><UserPlus className="w-4 h-4 mr-1" /> Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
